import { getCheckDomain } from '@/request/base';
import { useState, useCallback, useEffect } from 'react';

/**
 * 域名授权状态接口
 */
interface AuthState {
  isAuth: boolean | null; // 域名是否授权
  isChecking: boolean; // 是否正在检查域名授权
  data: AuthResponse | null; // 域名授权数据
}

/**
 * 域名授权响应接口
 */
interface AuthResponse {
  success: boolean; // 请求是否成功
  allow: boolean; // 是否允许访问
  expiry?: number; // 过期时间戳
  [key: string]: any;
}

/**
 * 域名授权配置常量
 */
const AUTH_CONFIG = {
  CACHE_KEY: 'DOMAIN_AUTH_DATA',
  CACHE_EXPIRY_DAYS: 30,
  ERROR_PATH: '/err',
  DEFAULT_CHANNEL: '',
  URL_PARAMS: {
    CHANNEL: 'wapid',
  },
  // 豁免规则
  EXEMPT_RULES: {
    // 精确匹配的域名
    exact: ['localhost'] as string[],
    // 精确匹配的路径
    exactPath: ['/s'] as string[],
  } as const,
} as const;

/**
 * 获取顶级域名
 */
const getTLD = (hostname: string): string => {
  const parts = hostname.split('.');
  // 处理特殊域名后缀
  const specialTLDs = [
    '.com.hk',
    '.co.uk',
    '.com.cn',
    '.org.uk',
    '.net.uk',
    '.gov.uk',
    '.ac.uk',
    '.sch.uk',
    '.me.uk',
    '.ltd.uk',
    '.plc.uk',
    '.net.cn',
    '.org.cn',
    '.gov.cn',
    '.edu.cn',
    '.mil.cn',
    '.com.tw',
    '.org.tw',
    '.idv.tw',
    '.co.jp',
    '.or.jp',
    '.ne.jp',
    '.ac.jp',
    '.ad.jp',
    '.go.jp',
    '.gr.jp',
    '.lg.jp',
    '.ed.jp',
    '.co.kr',
    '.or.kr',
    '.ac.kr',
    '.go.kr',
    '.ne.kr',
    '.pe.kr',
    '.re.kr',
    '.co.il',
    '.org.il',
    '.net.il',
    '.k12.il',
    '.ac.il',
    '.co.za',
    '.ac.za',
    '.org.za',
    '.net.za',
    '.web.za',
    '.co.nz',
    '.org.nz',
    '.net.nz',
    '.ac.nz',
    '.co.in',
    '.org.in',
    '.net.in',
    '.ac.in',
    '.edu.in',
    '.res.in',
    '.gov.in',
    '.co.id',
    '.or.id',
    '.ac.id',
    '.go.id',
    '.mil.id',
    '.co.th',
    '.ac.th',
    '.go.th',
    '.mi.th',
    '.or.th',
    '.co.ve',
    '.com.ve',
    '.org.ve',
    '.net.ve',
    '.web.ve',
    '.info.ve',
    '.co.ao',
    '.ed.ao',
    '.gv.ao',
    '.it.ao',
    '.og.ao',
    '.pb.ao',
  ];

  // 检查是否是特殊域名后缀
  for (const tld of specialTLDs) {
    if (hostname.endsWith(tld)) {
      return parts.slice(-3).join('.');
    }
  }

  // 默认处理：如果域名部分少于等于2，返回完整域名，否则返回最后两部分
  return parts.length <= 2 ? hostname : parts.slice(-2).join('.');
};

/**
 * 验证授权缓存有效性
 */
const validateCache = (cache: string): boolean => {
  try {
    const data = JSON.parse(cache) as AuthResponse;
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid cache data format');
    }

    const expired = !data?.expiry || data.expiry < Date.now();
    if (expired) {
      localStorage.removeItem(AUTH_CONFIG.CACHE_KEY);
      return false;
    }
    return true;
  } catch (error) {
    console.error('Failed to parse auth cache:', error);
    localStorage.removeItem(AUTH_CONFIG.CACHE_KEY);
    return false;
  }
};

/**
 * 处理授权响应
 */
const handleResponse = (res: AuthResponse, code: string): AuthState => {
  if (!res || typeof res !== 'object' || !res?.success || !res?.allow) {
    return { isAuth: false, isChecking: false, data: null };
  }

  const authData: AuthResponse = {
    ...res,
    expiry: res.cacheable ? Date.now() + res.expireTime : 0,
  };

  try {
    localStorage.setItem(`${code}_${AUTH_CONFIG.CACHE_KEY}`, JSON.stringify(authData));
  } catch (error) {
    console.error('Failed to save auth cache:', error);
  }

  return { isAuth: true, isChecking: false, data: authData };
};

/**
 * 域名授权检查Hook
 */
/**
 * 域名授权检查Hook
 * @param channel 渠道编码，默认为空字符串
 * @returns 包含域名授权状态的对象
 */
const useDomainAuth = (channel: string = AUTH_CONFIG.DEFAULT_CHANNEL) => {
  const [state, setState] = useState<AuthState>({
    isAuth: null,
    isChecking: true,
    data: null,
  });

  const verify = useCallback(async () => {
    if (window.location.pathname === AUTH_CONFIG.ERROR_PATH) {
      setState({
        isAuth: false,
        isChecking: false,
        data: null,
      });
      return;
    }

    try {
      const { hostname } = window.location;
      if (!hostname) throw new Error('Invalid domain');

      const tld = getTLD(hostname);

      // 需要pathname===于数组内的字符串
      const exactPath = AUTH_CONFIG.EXEMPT_RULES.exactPath.find((item) => window.location.pathname === item);

      // 检查精确匹配的域名或路径
      if (AUTH_CONFIG.EXEMPT_RULES.exact.includes(tld) || !!exactPath) {
        return setState({
          isAuth: true,
          isChecking: false,
          data: { allow: true, success: true },
        });
      }

      const cache = localStorage.getItem(`${channel}_${AUTH_CONFIG.CACHE_KEY}`) || '';
      if (cache && validateCache(cache)) {
        const data = JSON.parse(cache) as AuthResponse;
        return setState({
          isAuth: true,
          isChecking: false,
          data,
        });
      }

      const checkDomain = await getCheckDomain({ pDomain: tld, channelCode: channel });
      const res = checkDomain?.result?.bizValue;
      if (!res || typeof res !== 'object') {
        throw new Error('Invalid domain auth result');
      }

      const authRes: AuthResponse = {
        success: true,
        ...res,
      };

      const newState = handleResponse(authRes, channel);
      setState(newState);

      if (!newState.isAuth) {
        window.location.href = `${AUTH_CONFIG.ERROR_PATH}?wapid=${channel}`;
      }
    } catch (error) {
      console.error('Domain auth error:', error);
      setState({ isAuth: false, isChecking: false, data: null });
      window.location.href = `${AUTH_CONFIG.ERROR_PATH}?wapid=${channel}`;
    }
  }, [channel]);

  useEffect(() => {
    verify();
  }, [verify]);

  return { ...state, verify };
};

export default useDomainAuth;
